import { deleteDB, openDB } from "idb";
import type { DBSchema, IDBPDatabase } from "idb";

import type { OfflineDraftRecord, OfflineQueueRecord, OfflineSyncMetadataRecord } from "../../types/requisition";
import type {
  CachedItemCatalogRecord,
  CachedLubeOilChartRecord,
  CachedPurchaseOrderRecord,
  CachedRequisitionRecord,
  CachedVendorRecord,
  OfflineComponentTreeCacheRecord,
  OfflineSyncEventRecord,
  OfflineUserSessionRecord,
} from "../../types/offline";
import {
  collapseOfflineUuidDuplicates,
  getOfflineRecordPrimaryKey,
  normalizeOfflineRecord,
  normalizeOfflineStoreKey,
  normalizeUuid,
  OFFLINE_UUID_MIGRATION_CONFLICT,
  type OfflineUuidStoreName,
} from "./uuidCompatibility";

type OfflineStoreRecordByName = {
  requisitionDrafts: OfflineDraftRecord;
  requisitionQueue: OfflineQueueRecord;
  syncMetadata: OfflineSyncMetadataRecord;
  cachedRequisitions: CachedRequisitionRecord;
  cachedPurchaseOrders: CachedPurchaseOrderRecord;
  cachedVendors: CachedVendorRecord;
  cachedItemCatalog: CachedItemCatalogRecord;
  cachedComponentTree: OfflineComponentTreeCacheRecord;
  cachedLubeOilChart: CachedLubeOilChartRecord;
  syncEvents: OfflineSyncEventRecord;
  userSession: OfflineUserSessionRecord;
};

type StoreName = keyof OfflineStoreRecordByName;
type OfflineStoreRecord = OfflineStoreRecordByName[StoreName];

type OfflineStoreMap = {
  [TStore in StoreName]: Map<string, OfflineStoreRecordByName[TStore]>;
};

interface PurchaseOfflineDatabase extends DBSchema {
  requisitionDrafts: {
    key: OfflineDraftRecord["localDraftId"];
    value: OfflineDraftRecord;
  };
  requisitionQueue: {
    key: OfflineQueueRecord["id"];
    value: OfflineQueueRecord;
    indexes: {
      "by-event-sequence": OfflineQueueRecord["eventSequence"];
      "by-status": OfflineQueueRecord["status"];
      "by-local-draft-id": OfflineQueueRecord["localDraftId"];
    };
  };
  syncMetadata: {
    key: OfflineSyncMetadataRecord["key"];
    value: OfflineSyncMetadataRecord;
  };
  cachedRequisitions: {
    key: CachedRequisitionRecord["id"];
    value: CachedRequisitionRecord;
    indexes: {
      "by-vessel-id": CachedRequisitionRecord["vesselId"];
      "by-last-synced-at": CachedRequisitionRecord["lastSyncedAt"];
    };
  };
  cachedPurchaseOrders: {
    key: CachedPurchaseOrderRecord["id"];
    value: CachedPurchaseOrderRecord;
    indexes: {
      "by-vessel-id": CachedPurchaseOrderRecord["vesselId"];
      "by-last-synced-at": CachedPurchaseOrderRecord["lastSyncedAt"];
    };
  };
  cachedVendors: {
    key: CachedVendorRecord["id"];
    value: CachedVendorRecord;
    indexes: {
      "by-last-synced-at": CachedVendorRecord["lastSyncedAt"];
    };
  };
  cachedItemCatalog: {
    key: CachedItemCatalogRecord["id"];
    value: CachedItemCatalogRecord;
    indexes: {
      "by-vessel-id": CachedItemCatalogRecord["vesselId"];
      "by-last-synced-at": CachedItemCatalogRecord["lastSyncedAt"];
    };
  };
  cachedComponentTree: {
    key: OfflineComponentTreeCacheRecord["id"];
    value: OfflineComponentTreeCacheRecord;
    indexes: {
      "by-vessel-id": OfflineComponentTreeCacheRecord["vesselId"];
      "by-last-synced-at": OfflineComponentTreeCacheRecord["lastSyncedAt"];
    };
  };
  cachedLubeOilChart: {
    key: CachedLubeOilChartRecord["id"];
    value: CachedLubeOilChartRecord;
    indexes: {
      "by-vessel-id": CachedLubeOilChartRecord["vesselId"];
      "by-last-synced-at": CachedLubeOilChartRecord["lastSyncedAt"];
    };
  };
  syncEvents: {
    key: OfflineSyncEventRecord["id"];
    value: OfflineSyncEventRecord;
    indexes: {
      "by-status": OfflineSyncEventRecord["status"];
      "by-started-at": OfflineSyncEventRecord["startedAt"];
    };
  };
  userSession: {
    key: OfflineUserSessionRecord["key"];
    value: OfflineUserSessionRecord;
  };
}

const OFFLINE_DB_NAME = "purchase-module-offline";
const OFFLINE_DB_VERSION = 4;
const SYNC_METADATA_KEY: OfflineSyncMetadataRecord["key"] = "requisition_sync";

let memoryDatabase = createMemoryDatabase();
let databasePromise: Promise<IDBPDatabase<PurchaseOfflineDatabase>> | null = null;

function createMemoryDatabase(): OfflineStoreMap {
  return {
    requisitionDrafts: new Map<string, OfflineDraftRecord>(),
    requisitionQueue: new Map<string, OfflineQueueRecord>(),
    syncMetadata: new Map<string, OfflineSyncMetadataRecord>(),
    cachedRequisitions: new Map<string, CachedRequisitionRecord>(),
    cachedPurchaseOrders: new Map<string, CachedPurchaseOrderRecord>(),
    cachedVendors: new Map<string, CachedVendorRecord>(),
    cachedItemCatalog: new Map<string, CachedItemCatalogRecord>(),
    cachedComponentTree: new Map<string, OfflineComponentTreeCacheRecord>(),
    cachedLubeOilChart: new Map<string, CachedLubeOilChartRecord>(),
    syncEvents: new Map<string, OfflineSyncEventRecord>(),
    userSession: new Map<string, OfflineUserSessionRecord>(),
  };
}

function supportsIndexedDb(): boolean {
  return typeof indexedDB !== "undefined";
}

interface UpgradeObjectStore {
  getAll(): Promise<Array<Record<string, unknown>>>;
  clear(): Promise<unknown>;
  put(value: Record<string, unknown>): Promise<unknown>;
}

interface UpgradeTransaction {
  objectStore(storeName: StoreName): UpgradeObjectStore;
  abort(): void;
}

async function migrateStoreUuidKeys(
  transaction: UpgradeTransaction,
  storeName: StoreName,
): Promise<void> {
  const store = transaction.objectStore(storeName);
  const existingRecords = await store.getAll();
  const migratedRecords = collapseOfflineUuidDuplicates(
    storeName as OfflineUuidStoreName,
    existingRecords,
  );

  await store.clear();
  for (const record of migratedRecords) {
    await store.put(record);
  }
}

async function migrateUuidCompatibility(transaction: UpgradeTransaction): Promise<void> {
  const stores: StoreName[] = [
    "requisitionDrafts",
    "requisitionQueue",
    "syncMetadata",
    "cachedRequisitions",
    "cachedPurchaseOrders",
    "cachedVendors",
    "cachedItemCatalog",
    "cachedComponentTree",
    "cachedLubeOilChart",
    "syncEvents",
    "userSession",
  ];

  for (const storeName of stores) {
    await migrateStoreUuidKeys(transaction, storeName);
  }
}

async function getDatabase(): Promise<IDBPDatabase<PurchaseOfflineDatabase> | null> {
  if (!supportsIndexedDb()) {
    return null;
  }

  if (!databasePromise) {
    databasePromise = openDB<PurchaseOfflineDatabase>(OFFLINE_DB_NAME, OFFLINE_DB_VERSION, {
      async upgrade(database, oldVersion, _newVersion, transaction) {
        if (!database.objectStoreNames.contains("requisitionDrafts")) {
          database.createObjectStore("requisitionDrafts", { keyPath: "localDraftId" });
        }

        if (!database.objectStoreNames.contains("requisitionQueue")) {
          const queueStore = database.createObjectStore("requisitionQueue", { keyPath: "id" });
          queueStore.createIndex("by-event-sequence", "eventSequence", { unique: true });
          queueStore.createIndex("by-status", "status");
          queueStore.createIndex("by-local-draft-id", "localDraftId");
        }

        if (!database.objectStoreNames.contains("syncMetadata")) {
          database.createObjectStore("syncMetadata", { keyPath: "key" });
        }

        if (!database.objectStoreNames.contains("cachedRequisitions")) {
          const store = database.createObjectStore("cachedRequisitions", { keyPath: "id" });
          store.createIndex("by-vessel-id", "vesselId");
          store.createIndex("by-last-synced-at", "lastSyncedAt");
        }

        if (!database.objectStoreNames.contains("cachedPurchaseOrders")) {
          const store = database.createObjectStore("cachedPurchaseOrders", { keyPath: "id" });
          store.createIndex("by-vessel-id", "vesselId");
          store.createIndex("by-last-synced-at", "lastSyncedAt");
        }

        if (!database.objectStoreNames.contains("cachedVendors")) {
          const store = database.createObjectStore("cachedVendors", { keyPath: "id" });
          store.createIndex("by-last-synced-at", "lastSyncedAt");
        }

        if (!database.objectStoreNames.contains("cachedItemCatalog")) {
          const store = database.createObjectStore("cachedItemCatalog", { keyPath: "id" });
          store.createIndex("by-vessel-id", "vesselId");
          store.createIndex("by-last-synced-at", "lastSyncedAt");
        }

        if (!database.objectStoreNames.contains("cachedComponentTree")) {
          const store = database.createObjectStore("cachedComponentTree", { keyPath: "id" });
          store.createIndex("by-vessel-id", "vesselId");
          store.createIndex("by-last-synced-at", "lastSyncedAt");
        }

        if (!database.objectStoreNames.contains("cachedLubeOilChart")) {
          const store = database.createObjectStore("cachedLubeOilChart", { keyPath: "id" });
          store.createIndex("by-vessel-id", "vesselId");
          store.createIndex("by-last-synced-at", "lastSyncedAt");
        }

        if (!database.objectStoreNames.contains("syncEvents")) {
          const store = database.createObjectStore("syncEvents", { keyPath: "id" });
          store.createIndex("by-status", "status");
          store.createIndex("by-started-at", "startedAt");
        }

        if (!database.objectStoreNames.contains("userSession")) {
          database.createObjectStore("userSession", { keyPath: "key" });
        }

        if (oldVersion < 4) {
          try {
            await migrateUuidCompatibility(transaction as unknown as UpgradeTransaction);
          } catch (error) {
            try {
              transaction.abort();
            } catch {
              // A failed IndexedDB request may already have aborted the transaction.
            }
            throw error;
          }
        }
      },
    });
  }

  return databasePromise;
}

function getMemoryStore(storeName: StoreName): Map<string, OfflineStoreRecord> {
  return memoryDatabase[storeName] as Map<string, OfflineStoreRecord>;
}

function cloneOfflineRecord<TRecord extends OfflineStoreRecord>(value: TRecord): TRecord {
  return globalThis.structuredClone(value);
}

export async function upsertOfflineRecord<TRecord extends OfflineStoreRecord>(
  storeName: StoreName,
  key: string,
  value: TRecord,
): Promise<void> {
  const normalizedValue = normalizeOfflineRecord(value);
  const normalizedKey = getOfflineRecordPrimaryKey(
    storeName as OfflineUuidStoreName,
    normalizedValue as unknown as Record<string, unknown>,
  );
  const database = await getDatabase();
  if (database) {
    await database.put(storeName, normalizedValue as never);
    return;
  }

  getMemoryStore(storeName).set(
    normalizedKey || normalizeOfflineStoreKey(key),
    cloneOfflineRecord(normalizedValue),
  );
}

export async function getOfflineRecord<TRecord extends OfflineStoreRecord>(
  storeName: StoreName,
  key: string,
): Promise<TRecord | undefined> {
  const normalizedKey = normalizeOfflineStoreKey(key);
  const database = await getDatabase();
  if (database) {
    const value = (await database.get(storeName, normalizedKey)) as TRecord | undefined;
    return value ? normalizeOfflineRecord(value) : undefined;
  }

  const value = getMemoryStore(storeName).get(normalizedKey);
  return value ? cloneOfflineRecord(normalizeOfflineRecord(value as TRecord)) : undefined;
}

export async function deleteOfflineRecord(storeName: StoreName, key: string): Promise<void> {
  const normalizedKey = normalizeOfflineStoreKey(key);
  const database = await getDatabase();
  if (database) {
    await database.delete(storeName, normalizedKey);
    return;
  }

  getMemoryStore(storeName).delete(normalizedKey);
}

export async function listOfflineRecords<TRecord extends OfflineStoreRecord>(
  storeName: StoreName,
): Promise<TRecord[]> {
  const database = await getDatabase();
  if (database) {
    return ((await database.getAll(storeName)) as TRecord[]).map((value) => normalizeOfflineRecord(value));
  }

  return Array.from(getMemoryStore(storeName).values()).map((value) =>
    cloneOfflineRecord(normalizeOfflineRecord(value as TRecord)),
  );
}

export async function rewriteOfflineRequisitionServerReference(
  localDraftId: string,
  serverRequisitionId: string,
): Promise<string> {
  const canonicalLocalDraftId = normalizeOfflineStoreKey(localDraftId);
  const canonicalServerRequisitionId = normalizeUuid(serverRequisitionId);
  const updatedAt = new Date().toISOString();
  const database = await getDatabase();

  if (database) {
    const transaction = database.transaction(["requisitionDrafts", "requisitionQueue"], "readwrite");
    const draftStore = transaction.objectStore("requisitionDrafts");
    const queueStore = transaction.objectStore("requisitionQueue");
    const [draft, queuedIntents] = await Promise.all([
      draftStore.get(canonicalLocalDraftId),
      queueStore.getAll(),
    ]);

    if (draft) {
      await draftStore.put(
        normalizeOfflineRecord({
          ...draft,
          serverRequisitionId: canonicalServerRequisitionId,
          updatedAt,
        }),
      );
    }

    for (const intent of queuedIntents) {
      if (normalizeOfflineStoreKey(intent.localDraftId) === canonicalLocalDraftId) {
        await queueStore.put(
          normalizeOfflineRecord({
            ...intent,
            localDraftId: canonicalLocalDraftId,
            serverRequisitionId: canonicalServerRequisitionId,
            updatedAt,
          }),
        );
      }
    }
    await transaction.done;
    return canonicalServerRequisitionId;
  }

  const draft = memoryDatabase.requisitionDrafts.get(canonicalLocalDraftId);
  if (draft) {
    memoryDatabase.requisitionDrafts.set(
      canonicalLocalDraftId,
      cloneOfflineRecord({
        ...draft,
        serverRequisitionId: canonicalServerRequisitionId,
        updatedAt,
      }),
    );
  }
  for (const [key, intent] of memoryDatabase.requisitionQueue.entries()) {
    if (normalizeOfflineStoreKey(intent.localDraftId) === canonicalLocalDraftId) {
      memoryDatabase.requisitionQueue.set(
        normalizeOfflineStoreKey(key),
        cloneOfflineRecord({
          ...intent,
          localDraftId: canonicalLocalDraftId,
          serverRequisitionId: canonicalServerRequisitionId,
          updatedAt,
        }),
      );
    }
  }
  return canonicalServerRequisitionId;
}

// An explicit local draft edit may correct a project in an unsent/failed write.
// Keep event identity/order and all other content; never rewrite in-flight,
// completed, rejected-conflict or foreign-actor records.
export async function correctQueuedDraftProjectSelection(localDraftId: string, vesselId: string | number,
  authorUserId: number, projectId: string | null): Promise<void> {
  const localId = normalizeOfflineStoreKey(localDraftId);
  const database = await getDatabase();
  const repair = (intent: OfflineQueueRecord, draft: OfflineDraftRecord | undefined): OfflineQueueRecord | null => {
    if (!draft || draft.status !== "draft" || draft.authorUserId !== authorUserId
      || String(draft.vesselId) !== String(vesselId) || draft.project_id !== projectId
      || normalizeOfflineStoreKey(intent.localDraftId) !== localId
      || intent.authorUserId !== authorUserId || String(intent.vesselId) !== String(vesselId)
      || !["pending", "failed"].includes(intent.status)
      || intent.lastErrorCode === "REQUISITION_REJECTED_CONFLICT" || intent.lastErrorCode === OFFLINE_UUID_MIGRATION_CONFLICT
      || (intent.intentType !== "create_draft" && intent.intentType !== "update_draft")) return null;
    if (intent.intentType === "create_draft") {
      return { ...intent, payload: { ...intent.payload, project_id: projectId }, updatedAt: new Date().toISOString() };
    }
    return { ...intent, payload: { ...intent.payload, project_id: projectId }, updatedAt: new Date().toISOString() };
  };
  if (database) {
    const transaction = database.transaction(["requisitionDrafts", "requisitionQueue"], "readwrite");
    const draft = await transaction.objectStore("requisitionDrafts").get(localId);
    const store = transaction.objectStore("requisitionQueue");
    for (const intent of await store.getAll()) {
      const next = repair(intent, draft);
      if (next) await store.put(normalizeOfflineRecord(next));
    }
    await transaction.done;
  } else {
    const draft = memoryDatabase.requisitionDrafts.get(localId);
    for (const [key, intent] of memoryDatabase.requisitionQueue.entries()) {
      const next = repair(intent, draft);
      if (next) memoryDatabase.requisitionQueue.set(key, cloneOfflineRecord(next));
    }
  }
}

export function getSyncMetadataKey(): string {
  return SYNC_METADATA_KEY;
}

export async function resetOfflineStoresForTests(): Promise<void> {
  memoryDatabase = createMemoryDatabase();

  const existingDatabasePromise = databasePromise;
  databasePromise = null;

  if (!supportsIndexedDb() || !existingDatabasePromise) {
    return;
  }

  const database = await existingDatabasePromise;
  database.close();
  await deleteDB(OFFLINE_DB_NAME);
}
